import {
  VesselCardCustomCpValues,
  VesselCardDashboard,
  VesselCardModal as VesselCardModalName,
} from "../models/vessel-card.models";
import VesselCardEvaluationForm from "./VesselCardEvaluationForm";
import { FormEvent, useEffect, useRef, useState } from "react";

interface VesselCardModalProps {
  modal: Exclude<VesselCardModalName, null>;
  card: VesselCardDashboard;
  customCp: VesselCardCustomCpValues;
  onCustomCpChange: (values: VesselCardCustomCpValues) => void;
  onComment: (comment: string) => void;
  onClose: () => void;
  useMockData: boolean;
}

const customCpInputs: Array<{ key: keyof VesselCardCustomCpValues; label: string }> = [
  { key: "ballastEcoSpeed", label: "Ballast ECO speed" },
  { key: "ballastEcoConsumption", label: "Ballast ECO consumption" },
  { key: "ladenEcoSpeed", label: "Laden ECO speed" },
  { key: "ladenEcoConsumption", label: "Laden ECO consumption" },
  { key: "ballastFullSpeed", label: "Ballast FULL speed" },
  { key: "ballastFullConsumption", label: "Ballast FULL consumption" },
  { key: "ladenFullSpeed", label: "Laden FULL speed" },
  { key: "ladenFullConsumption", label: "Laden FULL consumption" },
];

const faqItems = [
  [
    "How is Expected Performance calculated?",
    "It comes from the latest vessel performance evaluation under clean-hull conditions, using vessel operational data when available or comparable sister-vessel data.",
  ],
  [
    "What is the difference between Expected Performance and Current Performance?",
    "Expected Performance is the clean-hull benchmark. Current Performance shows how the vessel's latest observed performance aligns with that benchmark.",
  ],
  [
    "When should I request a new Performance Evaluation?",
    "Request one when the evaluation is over three months old, current performance is at least 0.2 knots below expected, the basis is No_Data, or owners provide new paint or ESD information.",
  ],
  [
    "What does a High or Very High Fouling Factor mean?",
    "It indicates an increased likelihood that hull fouling is negatively affecting vessel performance. Further Vessel Performance assessment is recommended.",
  ],
  [
    "Why do some vessels show Design Level as the Performance Basis?",
    "There is not enough vessel-specific operational data, so expected performance uses comparable sister-vessel data at design level.",
  ],
  [
    "Why do some vessels show No_Data?",
    "Neither vessel-specific data nor a suitable design-level reference is available for a reliable assessment.",
  ],
  [
    "Can I override the CP Description?",
    "Yes. Select Custom CP and enter the eight desired speed and consumption values.",
  ],
  [
    "What is the purpose of TCO Descriptions?",
    "They provide alternative re-descriptions for vessels performing better than the original TC descriptions.",
  ],
  [
    "How should I interpret the CP Performance Indicator?",
    "It shows whether the vessel's expected performance is better than, aligned with, or below the selected CP benchmark.",
  ],
  [
    "Does Expected Performance include Auxiliary Engine consumption?",
    "Yes. It includes both Main Engine and Auxiliary Engine consumption.",
  ],
] as const;

const definitionItems = [
  [
    "Expected Performance",
    "Estimated clean-hull performance at the vessel's minimum and maximum consumption limits, including Main Engine and Auxiliary Engine consumption.",
  ],
  [
    "CP Description",
    "The selected speed and consumption benchmark: IMOS vessel CP description, IMOS VP input when available, or Custom CP values entered for this session. IMOS CP uses IFO when IMOS confirms a fitted scrubber, otherwise LSF, independently of the comparison toggle and performance basis. LSG and MGO are excluded. Vessels absent from IMOS and missing required fuel values stay unavailable.",
  ],
  [
    "Expected @ CP Consumption",
    "Expected clean-hull performance at the CP consumption value for direct comparison with the selected CP description.",
  ],
  [
    "CP Performance Indicator",
    "Comparison of expected performance with the selected CP description, ranging from Better Than CP to Outside Double Abt. Ratings use full-precision calculated speeds; displayed speeds are rounded.",
  ],
  [
    "Current Performance",
    "Current hull performance compared with the expected clean-hull benchmark using available operational data.",
  ],
  [
    "Fouling Factor",
    "Qualitative fouling-risk indicator based on operating history, coating condition, and time since dry dock. Low indicates minimal impact, Medium minor impact, and High or Very High potential significant impact.",
  ],
  [
    "Evaluated Date",
    "Date of the latest vessel performance evaluation. A new evaluation is recommended when the assessment is more than three months old.",
  ],
  [
    "Performance Basis",
    "Design Level uses comparable sister-vessel data, OBS_Actual uses vessel-specific operational data, and No_Data means the assessment lacks sufficient source data.",
  ],
  [
    "TCO Descriptions",
    "Both descriptions use DVP Expected Performance and keep expected consumption unchanged. Aggressive adds 0.5 knots to expected speed. Very Aggressive converts the 5% consumption tolerance into extra speed: (expected speed + 0.5) × (1 / 0.95)^(1 / 2.8). Very Aggressive speed is rounded down to one decimal place. Speeds are displayed to one decimal place.",
  ],
  [
    "Scrubber Selection",
    "OBS_Actual uses IMOS scrubber type and locks the selection; performance figures stay unchanged. A warning identifies disagreements with the performance summary. Design starts with VLSFO consumption. Yes selects HSFO and applies consumption × 1.03 plus 0.6 t/day below 66,000 DWT, 0.8 below 130,000 DWT, or 1.1 through 220,000 DWT, starting at 18,000 DWT. No restores VLSFO values. Design speeds stay unchanged. Missing or out-of-range DWT leaves corrected consumption unavailable. No Data can switch fuel labels but expected performance stays unavailable.",
  ],
] as const;

const idleExposureClassName = (exposure: string | null): string => {
  const normalized = exposure?.toLowerCase() ?? "";

  if (normalized.includes("very high") || normalized === "high") {
    return "font-semibold text-red-600 dark:text-red-400";
  }

  if (normalized === "medium") {
    return "font-semibold text-amber-600 dark:text-amber-400";
  }

  if (normalized === "low") {
    return "font-semibold text-emerald-600 dark:text-emerald-400";
  }

  return "";
};

// Hosts bookmark-style dialogs recovered from the PBIP and the OpsLink evaluation request form.
// Keeps the evaluation draft in its own component and blocks dismissal while its files and request are being saved.
const VesselCardModal = ({
  modal,
  card,
  customCp,
  onCustomCpChange,
  onComment,
  onClose,
  useMockData,
}: VesselCardModalProps) => {
  const [draftCp, setDraftCp] = useState(customCp);
  const [comment, setComment] = useState("");
  const [isRequestSubmitting, setIsRequestSubmitting] = useState(false);
  const dialogSectionRef = useRef<HTMLElement | null>(null);
  const closeGuardRef = useRef({ isRequestSubmitting, onClose });
  closeGuardRef.current = { isRequestSubmitting, onClose };

  useEffect(() => setDraftCp(customCp), [customCp, modal]);

  // Keeps keyboard navigation inside the dialog, including when upload disables the previously focused control.
  // Returning focus to the original action makes every existing bookmark dialog usable from the keyboard.
  useEffect(() => {
    const section = dialogSectionRef.current;
    const previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;

    if (!section) {
      return;
    }

    const getFocusableControls = () =>
      Array.from(
        section.querySelectorAll<HTMLElement>(
          'button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), a[href], summary, [tabindex]:not([tabindex="-1"])',
        ),
      ).filter((control) => control.getClientRects().length > 0);
    (getFocusableControls()[0] ?? section).focus();

    const handleDialogKeyboard = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();

        if (!closeGuardRef.current.isRequestSubmitting) {
          closeGuardRef.current.onClose();
        }

        return;
      }

      if (event.key !== "Tab") {
        return;
      }

      const controls = getFocusableControls();
      const first = controls[0];
      const last = controls[controls.length - 1];

      if (!first) {
        event.preventDefault();
        section.focus();

        return;
      }

      if (!section.contains(document.activeElement) || document.activeElement === section) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
      } else if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", handleDialogKeyboard, true);

    return () => {
      document.removeEventListener("keydown", handleDialogKeyboard, true);

      if (previousFocus?.isConnected) {
        previousFocus.focus();
      }
    };
  }, [modal]);

  const submitCustomCp = (event: FormEvent) => {
    event.preventDefault();
    onCustomCpChange(draftCp);
    onClose();
  };

  const submitComment = (event: FormEvent) => {
    event.preventDefault();
    onComment(comment);
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="vessel-card-modal-title"
      data-test={`vessel-card-${modal}-modal`}
      className="fixed inset-0 z-[200] flex items-center justify-center bg-gray-950/60 p-4"
      onMouseDown={(event) => !isRequestSubmitting && event.target === event.currentTarget && onClose()}
    >
      <section
        ref={dialogSectionRef}
        tabIndex={-1}
        data-test="vessel-card-modal-panel"
        className="max-h-[85vh] w-full max-w-2xl overflow-y-auto rounded-xl bg-white p-5 shadow-2xl dark:bg-gray-800"
      >
        <div className="mb-4 flex items-center justify-between gap-4">
          <h2 id="vessel-card-modal-title" className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            {modal === "customCp" && "Update Custom CP"}
            {modal === "comment" && "Add DVP comment"}
            {modal === "request" && "Request Performance Evaluation"}
            {modal === "email" && "Receive TCO descriptions by email"}
            {modal === "help" && "Vessel Card Help / FAQs"}
            {modal === "fouling" && "Fouling factor details"}
          </h2>
          <button
            type="button"
            disabled={isRequestSubmitting}
            aria-label="Close"
            data-test="vessel-card-modal-close-button"
            onClick={onClose}
            className="rounded px-2 py-1 text-xl text-gray-500 hover:bg-gray-100 disabled:opacity-50 dark:hover:bg-gray-700"
          >
            ×
          </button>
        </div>

        {modal === "customCp" && (
          <form onSubmit={submitCustomCp} data-test="vessel-card-custom-cp-form">
            <p className="mb-4 text-sm text-gray-500 dark:text-gray-400">
              Enter speed in knots and consumption in metric tonnes per day. Values apply to this page session;
              semantic-model-only indicators and expected curves are not recalculated.
            </p>
            <div className="grid gap-3 sm:grid-cols-2">
              {customCpInputs.map(({ key, label }) => (
                <label key={key} className="text-xs font-medium text-gray-600 dark:text-gray-300">
                  {label}
                  <input
                    type="number"
                    step="0.1"
                    min="0"
                    required
                    value={draftCp[key] ?? ""}
                    onChange={(event) =>
                      setDraftCp((current) => ({
                        ...current,
                        [key]: event.target.value === "" ? null : Number(event.target.value),
                      }))
                    }
                    data-test={`vessel-card-custom-cp-${key}-input`}
                    className="mt-1 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
                  />
                </label>
              ))}
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                data-test="vessel-card-custom-cp-cancel-button"
                onClick={onClose}
                className="rounded-md border border-gray-300 px-4 py-2 text-sm dark:border-gray-600"
              >
                Cancel
              </button>
              <button
                type="submit"
                data-test="vessel-card-custom-cp-apply-button"
                className="rounded-md bg-green px-4 py-2 text-sm font-semibold text-white"
              >
                Apply
              </button>
            </div>
          </form>
        )}

        {modal === "comment" &&
          (useMockData ? (
            <form onSubmit={submitComment} data-test="vessel-card-comment-form">
              <textarea
                required
                maxLength={2000}
                value={comment}
                onChange={(event) => setComment(event.target.value)}
                data-test="vessel-card-comment-input"
                className="min-h-32 w-full rounded-md border border-gray-300 p-3 text-sm dark:border-gray-600 dark:bg-gray-900"
                placeholder="Enter a DVP comment"
              />
              <div className="mt-4 flex justify-end gap-2">
                <button
                  type="button"
                  data-test="vessel-card-comment-cancel-button"
                  onClick={onClose}
                  className="rounded-md border px-4 py-2 text-sm"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  data-test="vessel-card-comment-add-button"
                  className="rounded-md bg-green px-4 py-2 text-sm font-semibold text-white"
                >
                  Add comment
                </button>
              </div>
            </form>
          ) : (
            <div
              data-test="vessel-card-comment-unavailable"
              className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200"
            >
              Comment reads are available from Snowflake, but the Power BI comment-write source is not Snowflake-owned
              and cannot be submitted from OpsLink yet.
            </div>
          ))}

        {modal === "request" && (
          <VesselCardEvaluationForm
            key={`${card.vessel.imo}-${useMockData}`}
            card={card}
            useMockData={useMockData}
            onClose={onClose}
            onSubmittingChange={setIsRequestSubmitting}
          />
        )}

        {modal === "email" && (
          <div className="space-y-4">
            <p data-test="vessel-card-email-summary" className="text-sm text-gray-600 dark:text-gray-300">
              The original action emails the CP, Aggressive, and Very Aggressive descriptions for
              <strong> {card.vessel.vesselName}</strong>.
            </p>
            <div
              data-test="vessel-card-external-action-unavailable"
              className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200"
            >
              This Power Automate email flow is not available from Snowflake, so the message cannot be sent from OpsLink
              yet.
            </div>
          </div>
        )}

        {modal === "help" && (
          <div className="space-y-5">
            <section className="space-y-4" data-test="vessel-card-faq-list">
              {faqItems.map(([question, answer]) => (
                <article key={question}>
                  <h3 className="text-sm font-semibold">{question}</h3>
                  <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">{answer}</p>
                </article>
              ))}
            </section>
            <section data-test="vessel-card-definition-list">
              <h3 className="mb-3 border-b border-gray-200 pb-2 text-base font-semibold dark:border-gray-700">
                Definitions
              </h3>
              <dl className="space-y-3">
                {definitionItems.map(([term, description]) => (
                  <div key={term}>
                    <dt className="text-sm font-semibold">{term}</dt>
                    <dd className="mt-1 text-sm text-gray-600 dark:text-gray-300">{description}</dd>
                  </div>
                ))}
              </dl>
            </section>
          </div>
        )}

        {modal === "fouling" && (
          <div>
            <p className="mb-4 text-sm">
              <strong>{card.foulingFactor ?? "Unknown"}:</strong>{" "}
              {card.foulingDetails ?? "No fouling details are available."}
            </p>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[620px] text-xs">
                <thead>
                  <tr className="text-left text-gray-500">
                    {[
                      "Idle from",
                      "Idle to",
                      "Days",
                      useMockData ? "Exposure" : "Exposure (raw)",
                      "Draft",
                      "Temp",
                      "Location",
                    ].map((label) => (
                      <th key={label} className="border-b p-2">
                        {label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {card.idleStays.length === 0 && (
                    <tr data-test="vessel-card-idle-stay-empty">
                      <td colSpan={7} className="p-4 text-center text-gray-500">
                        No idle stays are available.
                      </td>
                    </tr>
                  )}
                  {card.idleStays.map((stay, index) => (
                    <tr key={`${stay.idleFrom}-${index}`} data-test={`vessel-card-idle-stay-row-${index}`}>
                      <td className="p-2">{stay.idleFrom ?? "—"}</td>
                      <td className="p-2">{stay.idleTo ?? "—"}</td>
                      <td className="p-2">{stay.daysIdle ?? "—"}</td>
                      <td className={`p-2 ${idleExposureClassName(stay.foulingExposure)}`}>
                        {stay.foulingExposure ?? "—"}
                      </td>
                      <td className="p-2">{stay.draft ?? "—"}</td>
                      <td className="p-2">{stay.temperature ?? "—"}</td>
                      <td className="p-2">{stay.location ?? "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </section>
    </div>
  );
};

export default VesselCardModal;
